/**
 * styles.css — Thomas Fogarty tenant-scoped CSS.
 *
 * Injected AFTER src/styles/variables.css by init.js injectSharedStyles(), so these
 * :host(.ws-tenant-fogartywinery) rules override the base :host vars and the shared rich-card
 * scaffold. Moved verbatim out of variables.css (split per-winery for maintainability).
 */

/* Tenant-scoped CSS overrides go here only when a value cannot be driven
   by admin theme keys (i.e. not set inline by applyTheme() in init.js).

   Feature-card accent is one such value: it is not an admin theme key, so it
   is set per-variant here. Thomas Fogarty cards use the house dark-red
   (#6E1811) for titles/CTAs, matching the old createWineListing design. */
:host(.ws-tenant-fogartywinery) {
  --ws-card-accent: #6e1811;
  --ws-card-title-font: var(--ws-engraver, var(--ws-font));
  --ws-card-desc-font: var(--ws-avenir, var(--ws-font));
}

/* Chat bubbles — old .message-content parity: bot and user bubbles are both a
   borderless #f7f7f7 box with #1d1d1d Avenir 15px regular text (line-height
   1.3em); the corner beside the avatar is square (bot top-left, user top-right),
   the rest 8px. Kept ABOVE the .ws-typing rules so the typing indicator stays
   transparent. */
:host(.ws-tenant-fogartywinery) .ws-bot-bubble,
:host(.ws-tenant-fogartywinery) .ws-user-bubble {
  border: 0;
  border-radius: 8px;
  background: #f7f7f7;
  color: #1d1d1d;
  font-family: "Avenir", sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3em;
}

:host(.ws-tenant-fogartywinery) .ws-bot-bubble {
  border-top-left-radius: 0;
}

:host(.ws-tenant-fogartywinery) .ws-user-bubble {
  border-top-right-radius: 0;
}

/* Old user icon: material "person" in house red (#6E1811). The default SVG
   paints its path black with an inline style, so this needs !important. */
:host(.ws-tenant-fogartywinery) .ws-user-avatar svg,
:host(.ws-tenant-fogartywinery) .ws-user-avatar svg path {
  fill: #6e1811 !important;
}

:host(.ws-tenant-fogartywinery) .ws-login-menu {
  top: 30px;
  left: -26px;
  right: auto;
  margin-top: 0;
  min-width: 138px;
  padding: 6px 4px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: none;
  gap: 2px;
  z-index: 2000;
}

:host(.ws-tenant-fogartywinery) .ws-login-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  font-weight: 600;
  color: #000;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-account-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #ebedf0;
  color: #000;
}

:host(.ws-tenant-fogartywinery) .ws-checkout-section {
  padding: 24px;
}

:host(.ws-tenant-fogartywinery) .ws-checkout-section-title {
  font-family: "Avenir", sans-serif;
  font-weight: 700;
  color: #000;
}

/* Production shipping-form field labels use semibold, not heading weight. */
:host(.ws-tenant-fogartywinery) .ws-checkout-field label {
  font-family: "Avenir", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1d1d1d;
}

:host(.ws-tenant-fogartywinery) .ws-checkout-input::placeholder {
  color: #333;
  opacity: 1;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-phone-hint {
  color: #737373;
  font-size: 12px;
}

/* Production welcome copy renders the concierge name at normal weight,
   including when the tenant-config message contains markdown emphasis. */
:host(.ws-tenant-fogartywinery) .ws-welcome p strong,
:host(.ws-tenant-fogartywinery) .ws-welcome p b {
  font-weight: inherit;
}

/* Old Fogarty Splash layout: • bullets (not goosecross ✦), line above title. */
:host(.ws-tenant-fogartywinery) .ws-bot-bubble ul > li::before,
:host(.ws-tenant-fogartywinery) .ws-bot-bubble ol > li::before,
:host(.ws-tenant-fogartywinery) .ws-welcome ul > li::before,
:host(.ws-tenant-fogartywinery) .ws-welcome ol > li::before {
  content: "•";
  color: inherit;
}

:host(.ws-tenant-fogartywinery) .ws-bot-bubble ul ul > li::before,
:host(.ws-tenant-fogartywinery) .ws-bot-bubble ol ul > li::before,
:host(.ws-tenant-fogartywinery) .ws-bot-bubble ul ol > li::before,
:host(.ws-tenant-fogartywinery) .ws-bot-bubble ol ol > li::before,
:host(.ws-tenant-fogartywinery) .ws-welcome ul ul > li::before,
:host(.ws-tenant-fogartywinery) .ws-welcome ol ul > li::before,
:host(.ws-tenant-fogartywinery) .ws-welcome ul ol > li::before,
:host(.ws-tenant-fogartywinery) .ws-welcome ol ol > li::before {
  content: "⚬";
  color: #a29b8d;
}

:host(.ws-tenant-fogartywinery) .ws-bot-bubble hr,
:host(.ws-tenant-fogartywinery) .ws-welcome hr {
  display: block;
  width: 100%;
  height: 0;
  margin: 7px 0;
  border: 0;
  border-top: 2px solid #a29b8d;
}

:host(.ws-tenant-fogartywinery) .ws-bot-bubble h3,
:host(.ws-tenant-fogartywinery) .ws-welcome h3 {
  font-family: var(--ws-avenir, var(--ws-font));
  font-weight: 400;
}

:host(.ws-tenant-fogartywinery) .ws-bot-bubble li strong,
:host(.ws-tenant-fogartywinery) .ws-welcome li strong {
  color: inherit;
  font-weight: 700;
  font-size: 14px;
  line-height: 16.34px;
}

:host(.ws-tenant-fogartywinery) .ws-rich-review-button {
  border-radius: 1px;
}

/* Fogarty tasting cards use a gold glow + rounded shell instead of a hard
   border (old .tasting_card: box-shadow 0 0 10px rgba(189,133,39,.4)). */
:host(.ws-tenant-fogartywinery) .ws-tasting-card {
  border-color: transparent;
  border-radius: 12px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
}

/* Match the rendered production tasting cards. The legacy title font falls
   back to a regular serif in the production reference. */
:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-card-body .ws-card-title {
  font-family: "Times New Roman", Times, serif;
  line-height: normal;
  margin: 0 0 8px;
}

:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-card-desc-full {
  font-family: "Avenir", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #8e8e8e;
}

:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-card-learnmore {
  width: 100%;
  padding: 8px 12px;
  font-weight: 400;
}

:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-tasting-footer {
  margin-top: 10px;
}

:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-card-unit {
  color: #000;
  font-weight: 700;
}

:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-tasting-footer .ws-card-cta {
  padding: 9px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 1px;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
}

/* Follow-up questions are text-only; starting prompts retain their bolts. */
:host(.ws-tenant-fogartywinery) .ws-prompts--recommendations .ws-prompt-icon {
  display: none;
}

:host(.ws-tenant-fogartywinery) .ws-prompts--recommendations .ws-prompt-chip {
  width: 100%;
}

/* Old prod gave follow-up buttons one shared width (.same-opt-btn) instead of
   shrink-to-text, so every box in the group lines up at the same size. */
:host(.ws-tenant-fogartywinery) .ws-prompts--recommendations {
  max-width: 90%;
  align-items: stretch;
}

/* Old .promo-text-right "SHOP NOW!" was font-weight 700 (base is 400). */
:host(.ws-tenant-fogartywinery) .ws-banner-cta {
  font-weight: 700;
}

/* Chat input bar — old #user-message parity: one light-gray 38px field (input
   + mic, 6px radius), Avenir 16px regular #797264 text, a 26px maroon mic
   circle with a white glyph (old mic_off svg), send arrow 15px to the right.
   Mic colours are literal: applyTheme() writes --ws-mic-* inline on the host. */
:host(.ws-tenant-fogartywinery) .ws-input-area {
  padding: 10px 10px 0;
  gap: 15px;
}

:host(.ws-tenant-fogartywinery) .ws-input-wrap {
  box-sizing: border-box;
  min-height: 38px;
  padding: 4px 4px 4px 15px;
  border-radius: 6px;
  background: rgba(192, 195, 196, 0.18);
}

:host(.ws-tenant-fogartywinery) .ws-input {
  font-family: "Avenir", sans-serif !important;
  font-weight: 400 !important;
  line-height: 20px;
}

:host(.ws-tenant-fogartywinery) .ws-input::placeholder {
  color: #797264;
  opacity: 1;
}

:host(.ws-tenant-fogartywinery) .ws-btn-mic {
  width: 26px;
  height: 26px;
  /* The UA button padding (6px each side) squeezed the glyph to ~14px. */
  padding: 0;
  background: #6e1811;
  color: #ffffff;
}

/* 21px renders the mic glyph ~12×16.5px — the same size as old mic_off. */
:host(.ws-tenant-fogartywinery) .ws-btn-mic svg {
  flex-shrink: 0;
  width: 21px;
  height: 21px;
}

:host(.ws-tenant-fogartywinery) .ws-btn-send {
  padding: 0;
  color: #6e1811;
}

/* Legacy .loader: a 22px-wide row of gray dots revealed in four steps. */
:host(.ws-tenant-fogartywinery) .ws-typing-dots {
  width: 22px;
  background: radial-gradient(circle closest-side, #424245 90%, transparent)
    0 / 33.3333333333% 100% space;
}

:host(.ws-tenant-fogartywinery) .ws-typing {
  background: transparent;
  border: 0;
  box-shadow: none;
}

:host(.ws-tenant-fogartywinery) .ws-typing:has(.ws-fogarty-waiting) {
  width: 100%;
  padding: 0;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-waiting {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-waiting p {
  width: 70%;
  min-width: 70%;
  margin: 0;
  font-family: "Avenir", sans-serif;
  font-size: 15px;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-waiting img {
  height: 100px;
  max-height: 100px;
  min-width: 0;
  object-fit: contain;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-cart-overlay {
  position: absolute;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0008;
  border-radius: inherit;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-cart-toast {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 360px;
  max-width: calc(100% - 40px);
  box-sizing: border-box;
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  color: var(--ws-card-accent);
  box-shadow: 0 4px 16px #0003;
  font-family: var(--ws-font);
  font-size: 14px;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-cart-toast strong {
  font-size: 16px;
  font-weight: 400;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-cart-toast-icon {
  display: flex;
  flex-shrink: 0;
}

:host(.ws-tenant-fogartywinery) .ws-fogarty-cart-toast p {
  margin: 4px 0 0;
  color: #333;
}

:host(.ws-tenant-fogartywinery) .ws-cart-actions {
  gap: 5px;
  align-items: flex-start;
}

:host(.ws-tenant-fogartywinery) .ws-cart-panel .ws-cart-total {
  font-family: "Avenir", sans-serif;
  font-size: 15px;
}

:host(.ws-tenant-fogartywinery) .ws-cart-panel .ws-cart-total > span:first-child {
  color: #737373;
  font-weight: 400;
}

:host(.ws-tenant-fogartywinery) .ws-cart-panel .ws-cart-total > span:last-child {
  color: #000;
  font-weight: 600;
}

:host(.ws-tenant-fogartywinery) .ws-cart-action-btn {
  border: 1px solid var(--ws-card-accent);
  border-radius: 2px;
  padding: 8px 12px;
  min-height: 35px;
  margin-right: 5px;
  font-family: "Avenir", sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 16px;
  background: #fff;
  color: var(--ws-card-accent);
}

:host(.ws-tenant-fogartywinery) .ws-cart-action-btn:hover {
  background: #f4e3c1;
}

:host(.ws-tenant-fogartywinery) .ws-cart-action-btn.is-selected {
  background: var(--ws-card-accent);
  color: #fff;
}

/* Bottom-pin the action row across equal-height carousel cards so CTAs line up
   regardless of how much content sits above them (wine: price+CTA; tasting:
   footer). Base-card structure is `.ws-card > .ws-card-body > … > cta/footer`.
   SAME pattern applied per-winery on the Valle branch — candidate to promote to
   a single shared base rule when the winery branches merge into dev. */
:host(.ws-tenant-fogartywinery) .ws-carousel-item.ws-card {
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-fogartywinery) .ws-carousel-item .ws-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
/* wine + membership: price + CTA sink together to the card bottom */
:host(.ws-tenant-fogartywinery) .ws-wine-card .ws-card-price,
:host(.ws-tenant-fogartywinery) .ws-membership-card .ws-card-price {
  margin-top: auto;
}
/* tasting + event: the price/CTA footer pins to the bottom */
:host(.ws-tenant-fogartywinery) .ws-tasting-footer,
:host(.ws-tenant-fogartywinery) .ws-event-footer {
  margin-top: auto;
}

/* Keep "$xx" + "Per Person" on one line in the event/tasting footer. The long
   uppercase CTA ("PURCHASE TICKETS" ≈ 156px) otherwise eats the 216px footer
   row and squeezes the price block to ~50px, wrapping the unit to two lines.
   (Card is 240px — same as the old; this is just the long label, not width.)
   Stop the price block from shrinking + nowrap the unit, tighten the gap, and
   trim the CTA's footprint so both sit inline. */
:host(.ws-tenant-fogartywinery) .ws-event-footer,
:host(.ws-tenant-fogartywinery) .ws-tasting-footer {
  gap: 8px;
  margin-top: 24px;
}
:host(.ws-tenant-fogartywinery) .ws-event-footer .ws-price-block,
:host(.ws-tenant-fogartywinery) .ws-tasting-footer .ws-price-block {
  flex-shrink: 0;
}
:host(.ws-tenant-fogartywinery) .ws-event-footer .ws-card-unit,
:host(.ws-tenant-fogartywinery) .ws-tasting-footer .ws-card-unit {
  white-space: nowrap;
}
:host(.ws-tenant-fogartywinery) .ws-event-footer .ws-card-cta,
:host(.ws-tenant-fogartywinery) .ws-tasting-footer .ws-card-cta {
  padding: 10px 14px;
  font-size: 14px;
}

/* Reserved events ("Weddings, Corporate & Private Events") carry no price, so
   the footer holds only the CTA. With justify-content:space-between a lone child
   sits at the LEFT — push it right (the old always rendered an empty price block
   to keep "Inquire Now" right-aligned). Harmless for priced events (already
   right via space-between). */
:host(.ws-tenant-fogartywinery) .ws-event-footer .ws-card-cta {
  margin-left: auto;
}

/* Membership card (Fogarty override): match old createWineMembership /
   .card-box — uppercase serif title, centered Learn more pill, Join Now pinned
   bottom-right (.innerfooter justify-content:right). */
:host(.ws-tenant-fogartywinery) .ws-membership-card .ws-card-title {
  font-family: "Times New Roman", Times, serif;
  text-transform: uppercase;
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  padding: 8px;
  margin: 0;
  white-space: pre-line; /* render the <br>→newline from the title as a break */
}
/* Old .innercardbox: column + space-between so Join Now stays at the bottom
   across equal-height carousel cards. */
:host(.ws-tenant-fogartywinery) .ws-fg-mem-body {
  justify-content: space-between;
  gap: 6px;
  padding: 8px;
  flex-grow: 1;
}
:host(.ws-tenant-fogartywinery) .ws-membership-card .ws-card-desc-full {
  font-size: 14px;
  color: #1d1d1d;
  line-height: 1.4;
}
/* Old .learn-more: pill + soft maroon shadow, centered via margin:auto. */
:host(.ws-tenant-fogartywinery) .ws-membership-card .ws-card-learnmore {
  width: 100%;
  font-weight: 400;
  margin: auto;
  margin-bottom: 10px;
  padding: 8px 12px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 20px;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-accent);
  font-size: 15px;
  line-height: 1;
}
/* Old .innerfooter — Join Now always bottom-right. */
:host(.ws-tenant-fogartywinery) .ws-fg-mem-footer {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  margin-top: 24px;
  font-weight: 600;
}
:host(.ws-tenant-fogartywinery) .ws-fg-mem-cta {
  margin-top: 0;
  padding: 10px 14px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 4px;
  background: var(--ws-card-accent);
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
/* No-cover fallback: padded house logo (old defaultLogo + padding:19px). */
:host(.ws-tenant-fogartywinery) .ws-card-img.ws-fg-mem-logo {
  aspect-ratio: 16 / 9;
  object-fit: contain;
  padding: 19px;
  background: #ffffff;
}
/* Cover image padding like old .card-box-cover-image. */
:host(.ws-tenant-fogartywinery) .ws-membership-card .ws-card-head {
  padding: 6px;
}
:host(.ws-tenant-fogartywinery) .ws-membership-card .ws-card-img:not(.ws-fg-mem-logo) {
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 6px;
  background: transparent;
}

/* Guided-flow chips (Fogarty) — match old .option-button: house maroon text +
   border, sized to their text (left-aligned, not full-width), tan hover, and a
   filled-maroon "selected" state on click (old changeBKG / .selected-button).
   The bolt icon uses currentColor, so it follows the text color automatically. */
/* Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
:host(.ws-tenant-fogartywinery) .ws-prompt-chip {
  color: var(--ws-card-accent);
  border-color: var(--ws-card-accent);
  font-family: "Avenir", sans-serif;
  font-size: 15px;
  /* Old .option-button (follow-ups are .option-button.same-opt-btn) was 600. */
  font-weight: 600;
  line-height: 16px;
  gap: 5px;
  min-height: 35px;
  border-radius: 2px;
}
/* Base `#ws-chat-panel button { font-family: var(--ws-font) }` is an ID rule, so
   it outranks the class rule above and chip <button>s rendered in Open Sans.
   Re-assert Avenir at ID specificity — font only, so the hover/.is-selected
   colour rules keep winning. */
:host(.ws-tenant-fogartywinery) #ws-chat-panel .ws-prompt-chip {
  font-family: "Avenir", sans-serif;
}
:host(.ws-tenant-fogartywinery) .ws-prompt-chip:hover {
  background: var(--ws-card-accent);
  color: #fff;
}
:host(.ws-tenant-fogartywinery) .ws-prompt-chip.is-selected {
  background: var(--ws-card-accent);
  border-color: var(--ws-card-accent);
  color: #ffffff;
}
/* The bolt icon span pins its own color to --ws-primary (ChatPanel.css), which
   overrides the white text on a selected chip and leaves the icon invisible on
   the filled background. Let the icon follow the chip's text color when selected. */
:host(.ws-tenant-fogartywinery) .ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

/* ── Fogarty wine listing card (parity: createWineListing) ────────── */
:host(.ws-tenant-fogartywinery) .ws-fg-winecard {
}
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-head {
  position: relative;
  height: 180px;
  min-height: 180px;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-soldout {
  position: absolute;
  right: 8px;
  top: 8px;
  text-align: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  width: fit-content;
  background-color: #000000ab;
  color: #fff;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-info {
  margin-top: 12px;
}

/* Old .wine_card_title: 16px uppercase house-red serif. EngraversRomanBTS never
   loaded in production, so it rendered as a regular serif — match that (same
   as the tasting/event card titles). 5px gap = old `p` margin. */
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-title {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 400;
  font-family: "Times New Roman", Times, serif;
  color: var(--ws-card-accent, #6e1811);
  text-transform: uppercase;
  line-height: normal;
  min-height: 27px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Old .wine_card_desc: label + Appellation 15px semibold #1d1d1d Avenir, 5px
   apart (old `p` margin); Varietal + Alcohol drop to 400 (-light). */
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-desc {
  margin: 5px 0 0;
  font-family: "Avenir", sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 120%;
  color: #1d1d1d;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-desc-light {
  font-weight: 400;
}
/* Equal-height wine cards — old .chat-carousel .card was a flex column inside a
   stretching flex row. cards.css gives .ws-rich-winecard height:100%, and a
   non-auto height switches off the track's align-self:stretch (the track has no
   definite height), so each card sized to its own title/meta. Reset to auto so
   every card stretches to the tallest, and let the body fill the card. */
:host(.ws-tenant-fogartywinery) .ws-carousel-item.ws-fg-winecard {
  height: auto;
  min-height: 0;
  align-self: stretch;
}
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-body {
  margin-top: 10px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Old .tasting-notes-link: full-width pill below the wine details. */
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-sizing: border-box;
  width: 100%;
  padding: 8px 12px;
  margin: 10px 0;
  border: 1px solid var(--ws-card-accent, #6e1811);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-accent, #6e1811);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
/* Old .card justify-content:space-between put the free space between the wine
   details and the Tasting notes pill, so pill + price/Add to cart line up at the
   bottom of every card. Sold-out cards (no pill) keep the details' own
   margin-top:auto. One auto margin only — two would split the space. */
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-tasting {
  margin-top: auto;
}
:host(.ws-tenant-fogartywinery)
  .ws-fg-winecard-tasting
  + .ws-fg-winecard-product-details {
  margin-top: 0;
}
.ws-fg-tasting svg {
  flex: 0 0 auto;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-product-details {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.ws-fg-price {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-accent, #6e1811);
}
.ws-fg-unit {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #000;
}
.ws-fg-add {
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  background: var(--ws-card-accent, #6e1811);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ws-cp-action {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ws-cp-delete {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--ws-border);
  border-radius: 4px;
  background: #fff;
  color: #0a0a0a;
  cursor: pointer;
}
.ws-cp-qty {
  flex: 1 1 auto;
  height: 40px;
  /* Right padding leaves room for the custom chevron so the value + icon
     never crowd each other (native appearance gave no controllable spacing). */
  padding: 0 34px 0 12px;
  border: 1px solid var(--ws-border);
  border-radius: 4px;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23574D39' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  color: var(--ws-text);
  font-size: 14px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}
.ws-cp-update {
  flex: 0 0 auto;
  padding: 0 16px;
  height: 40px;
  border: none;
  border-radius: 4px;
  background: var(--ws-card-accent, #6e1811);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-product-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

:host(.ws-tenant-fogartywinery) .ws-fg-winecard-product-footer p:nth-child(2),
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-product-footer p:nth-child(3),
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-product-footer p:nth-child(2),
:host(.ws-tenant-fogartywinery) .ws-fg-winecard-product-footer p:nth-child(3) {
  font-size: 12px;
  color: #000;
  margin-block: 0;
  font-weight: 400;
}

/* Prod reference shows the wine price in black (base .ws-fg-price is house red). */
:host(.ws-tenant-fogartywinery) .ws-fg-winecard .ws-fg-price {
  color: #000;
}

/* Qty controls — old .product-action parity (ss2) */
:host(.ws-tenant-fogartywinery) .ws-fg-product-action,
:host(.ws-tenant-fogartywinery) .ws-fg-card .product-action {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-left: auto;
  white-space: nowrap;
  width: auto;
}

.ws-cp-qty-wrap,
:host(.ws-tenant-fogartywinery) .ws-fg-card .product-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
}

:host(.ws-tenant-fogartywinery) .ws-fg-card .product-action button,
:host(.ws-tenant-fogartywinery) .ws-fg-delete,
:host(.ws-tenant-fogartywinery) .ws-fg-dec,
:host(.ws-tenant-fogartywinery) .ws-fg-inc {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid #e5e5e5;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}

:host(.ws-tenant-fogartywinery) .ws-fg-delete svg,
:host(.ws-tenant-fogartywinery) .ws-fg-card .delete-cart svg {
  width: 16px;
  height: 16px;
  display: block;
}

:host(.ws-tenant-fogartywinery) .ws-fg-cart-qty,
:host(.ws-tenant-fogartywinery) .ws-fg-card .cart-qty {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 30px;
  padding: 2px 10px;
  margin: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #0a0a0a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
}

:host(.ws-tenant-fogartywinery) .ws-fg-view-basket {
  border-color: var(--ws-primary);
  color: var(--ws-primary);
  flex: 0 0 auto;
  width: max-content;
}

:host(.ws-tenant-fogartywinery) .ws-fg-view-basket:hover {
  background: color-mix(in srgb, var(--ws-primary) 8%, transparent);
}



/* Event Card */
:host(.ws-tenant-fogartywinery) .ws-tasting-card .ws-card-body .ws-card-title,
:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-body .ws-card-title {
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 400;
}

:host(.ws-tenant-fogartywinery) .ws-event-card {
  padding: 8px;
  border-radius: 12px;
  font-family: "Avenir", sans-serif;
}

:host(.ws-tenant-fogartywinery) .ws-carousel:has(.ws-event-card) .ws-carousel-nav {
  width: 24px;
  height: 24px;
  border: 1px solid #000;
  border-radius: 50%;
  background: #fff;
  color: #000;
  box-shadow: none;
  font-size: 14px;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-body {
  padding: 12px 0 0;
  gap: 0;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-body .ws-card-title {
  font-family: "Times New Roman", Times, serif;
  line-height: normal;
  margin: 0 0 8px;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-head {
  max-height: 180px;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-img {
  aspect-ratio: 16/9;
  object-fit: cover;
  border-radius: 6px;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-meta {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #8e8e8e;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-learnmore {
  width: 100%;
  margin-top: auto;
  margin-bottom: 10px;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 400;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-meta:has(+ .ws-card-learnmore) {
  margin-bottom: 8px;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-event-footer {
  margin-top: 0;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-price {
  font-size: 16px;
  font-weight: 700;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-unit {
  font-size: 12px;
  font-weight: 700;
  color: #000;
}

:host(.ws-tenant-fogartywinery) .ws-event-card .ws-event-footer .ws-card-cta {
  padding: 9px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 1px;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
}

/* Old createEvents meta lines: only .event_date_range is bold black; .event_time
   and .event_location stay gray, location with an 8px gap below. Keyed off the
   meta kind, not position — reserved events ("Weddings, Corporate & Private
   Events") have no date, so the winery-name location comes first but stays gray. */
:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-meta--date {
  color: #000000;
  font-weight: 700;
}
:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-meta--time {
  font-weight: 500;
}
:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-meta--location {
  margin-bottom: 8px;
}

/* Old .event_description: 12px gray, no top margin, on the widget's inherited
   #winespeakAI line-height (1.3em ≈ 20.8px). */
:host(.ws-tenant-fogartywinery) .ws-event-card .ws-card-desc {
  margin: 0;
  font-size: 12px;
  line-height: 20.8px;
  color: #8e8e8e;
}


/* Tasting Notes Drawer */

:host(.ws-tenant-fogartywinery) .ws-wine-drawer-intro,
:host(.ws-tenant-fogartywinery) .ws-drawer-pairing-box,
:host(.ws-tenant-fogartywinery) .ws-drawer-fp-card {
  background: rgb(255, 240, 239);
}
